import React, { useCallback, useState } from 'react';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useModal } from 'components/modals/ModalContext';
import { useTranslation } from 'react-i18next';
import CustomFieldModal from 'components/forms/custom_fields/CustomFieldModal';
import { LinkedCustomFieldObjectType } from 'components/forms/custom_fields/CustomFieldTypes';

interface CustomFieldListProps {
    objectType?: LinkedCustomFieldObjectType;
}

const CustomFieldList: React.FC<CustomFieldListProps> = ({
    objectType
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const [refetchList, setRefetchListState] = useState<(() => void) | null>(null);

    // Allow refreshing of the list
    const setRefetchList = useCallback((newRefetchList: () => void) => {
        setRefetchListState(() => newRefetchList);
    }, []);

    // Show one summary row per object type for the inspection fields
    const isSummaryList = objectType === 'asset_type_inspection';

    // Specify the options of this list:
    const query = isSummaryList ? {
        endpoint: 'get_custom_field_set_summary_list',
        defaultParams: {
            linked_object_type: String(objectType),
        },
        object: 'custom_field'
    } : {
        endpoint: 'get_custom_field_list',
        defaultParams: {
            deleted: false,
            linked_object_type: String(objectType),
        },
        object: 'custom_field'
    };
    const groupByField = isSummaryList ? undefined : { field: 'linked_object_name' }
    const objectName = 'custom_field.general.object_name';

    // Render the field type
    const renderFieldType = (row: any) => {
        return <>{t(`custom_field.field_type.${row.field_type}`)}</>
    }

    // Specify the columns of this list:
    const columns: ListColumnType[] = isSummaryList ? [
        {
            width: '2fr',
            field: 'linked_object_name',
            label: 'custom_field.general.object_type_label',
        },
        {
            width: '1fr',
            field: 'field_count',
            label: 'custom_field.general.field_count_label',
        },
    ] : [
        {
            width: '2fr',
            field: 'label',
            label: 'custom_field.general.name_label',
        },
        {
            width: '1fr',
            field: 'field_type',
            label: 'custom_field.general.field_type_label',
            render: (_, row) => renderFieldType(row)
        },
    ];

    const handleRowClick = (row: any) => {
        initializeModal(
            <CustomFieldModal
                linkedObjectType={row.linked_object_type}
                linkedObjectValue={String(row.linked_object_value)}
                onSuccess={() => {
                    if (refetchList) {
                        refetchList();
                    }
                }}
            />,
            {
                modalSize: 'medium-large',
                title: 'custom_field.general.edit_custom_fields_title'
            }
        );
    };

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            groupByField={groupByField}
            showSearch={false}
            isPaginated={false}
            getFormModal={() => (
                objectType ? (
                    <CustomFieldModal
                        linkedObjectType={objectType}
                        onSuccess={() => {
                            if (refetchList) {
                                refetchList();
                            }
                        }}
                    />
                ) : <></>
            )}
            getModalSize={() => 'medium-large'}
            getFormModalTitle={() => t('custom_field.general.add_custom_fields_title')}
            onRowClick={handleRowClick}
            setRefetchList={setRefetchList}
        />
    );
};

export default CustomFieldList;